const PAGE_WRAPPER_SELECTOR = '.page-wrapper';
const SQUEEZE_QUERY = window.matchMedia('(min-width: 990px)');

/**
 * Returns the current page scroll container.
 * In the squeeze layout (desktop ≥990px), `.page-wrapper` is the scroll container instead of
 * `document.scrollingElement`. On mobile, the document root scrolls natively so the address bar
 * can hide/show.
 *
 * @returns {Element} The scroll container element
 */
function getScrollContainer() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? document.scrollingElement ?? document.documentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

/**
 * Returns the current scroll position of the page scroll container.
 *
 * @returns {number} The scrollTop value
 */
function getScrollTop() {
  return getScrollContainer().scrollTop;
}

/**
 * Scrolls the page scroll container to the specified position.
 *
 * @param {ScrollToOptions} options - The scroll options (top, left, behavior)
 */
function scrollTo(options) {
  getScrollContainer().scrollTo(options);
}

/**
 * Manual scroll restoration for `.page-wrapper` as scroll container.
 *
 * On desktop (≥990px), the browser restores scroll on `document.scrollingElement`, which
 * has `overflow: hidden` in the squeeze layout and can never scroll. We disable native
 * restoration and handle it via `history.state` instead.
 *
 * On mobile (<990px), `document.scrollingElement` IS the scroll container, so native
 * restoration works perfectly — leave it enabled to avoid a one-frame flash from rAF.
 *
 * Save: `pagehide` — fires exactly once per navigation, captures the exact scroll position
 * at the moment the user leaves. More accurate than debounced scroll (which can be stale
 * if the user scrolls and immediately clicks a link).
 *
 * Restore: unconditional `pageshow` — Horizon uses cross-document view transitions, so
 * `pageshow` fires on every navigation (both bfcache and fresh loads). `popstate` is not
 * used because it doesn't fire for cross-document back navigation.
 */
const HOF_SCROLL_PREFIX = 'hof-scroll:';

function hofScrollKey() {
  return HOF_SCROLL_PREFIX + location.pathname + location.search;
}

try {
  history.scrollRestoration = 'manual';
} catch (_) {}

function containerTopOffset(container) {
  if (container === document.scrollingElement || container === document.documentElement) return 0;
  return container.getBoundingClientRect().top;
}

function readAnchor() {
  const container = getScrollContainer();
  const top = container.scrollTop;
  const base = containerTopOffset(container);
  let anchor = null;
  const sections = document.querySelectorAll('#MainContent > .shopify-section[id]');
  for (const section of sections) {
    const rectTop = section.getBoundingClientRect().top - base;
    if (rectTop <= 1) anchor = { id: section.id, off: Math.round(-rectTop) };
    else break;
  }
  return { top: Math.round(top), id: anchor ? anchor.id : '', off: anchor ? anchor.off : 0 };
}

function saveScrollPosition() {
  const data = readAnchor();
  try {
    const currentState = typeof history.state === 'object' && history.state !== null ? history.state : {};
    history.replaceState({ ...currentState, scrollTop: data.top, hofAnchor: data }, '');
  } catch (_) {}
  try {
    sessionStorage.setItem(hofScrollKey(), JSON.stringify(data));
  } catch (_) {}
}

window.addEventListener('pagehide', saveScrollPosition);

document.addEventListener(
  'click',
  (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href], [href], product-card, .product-card') : null;
    if (link) saveScrollPosition();
  },
  true
);

let hofSaveTimer = 0;
let hofRestoring = false;
document.addEventListener(
  'scroll',
  () => {
    if (hofRestoring) return;
    clearTimeout(hofSaveTimer);
    hofSaveTimer = setTimeout(saveScrollPosition, 120);
  },
  { capture: true, passive: true }
);

function targetFor(data) {
  const container = getScrollContainer();
  if (data.id) {
    const el = document.getElementById(data.id);
    if (el) {
      return el.getBoundingClientRect().top - containerTopOffset(container) + container.scrollTop + data.off;
    }
  }
  return data.top;
}

/**
 * @param {number} savedScrollTop
 */
function restoreSavedScrollTop(savedScrollTop) {
  if (!Number.isFinite(savedScrollTop) || savedScrollTop < 0) return;
  const container = getScrollContainer();
  const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
  container.scrollTo({ top: Math.min(savedScrollTop, maxScrollTop), behavior: 'instant' });
}

/**
 * Puts the page back where the visitor was, anchored on the section he was looking at,
 * and keeps it there while videos, carousels and images above finish loading.
 *
 * @param {{top:number,id:string,off:number}} data
 */
function restoreWithRetry(data) {
  const root = document.documentElement;
  const main = document.getElementById('MainContent');
  const reveal = () => root.classList.remove('hof-restoring');
  let cancelled = false;
  const stop = () => {
    cancelled = true;
  };
  ['wheel', 'touchstart', 'keydown', 'mousedown'].forEach((type) =>
    window.addEventListener(type, stop, { once: true, passive: true })
  );
  if (main) main.style.minHeight = Math.round(data.top + window.innerHeight * 2) + 'px';
  hofRestoring = true;
  const start = performance.now();
  let stableSince = 0;
  let revealed = false;
  const finish = () => {
    hofRestoring = false;
    reveal();
    if (main) main.style.minHeight = '';
  };
  const tick = () => {
    if (cancelled) return finish();
    const target = targetFor(data);
    const container = getScrollContainer();
    if (Math.abs(container.scrollTop - target) >= 1) restoreSavedScrollTop(target);
    const now = performance.now();
    const reached = Math.abs(getScrollContainer().scrollTop - target) < 2;
    if (reached && !revealed && document.readyState !== 'loading') {
      revealed = true;
      reveal();
    }
    if (reached) {
      if (!stableSince) stableSince = now;
      if (now - stableSince > 1200 && document.readyState === 'complete') return finish();
    } else {
      stableSince = 0;
    }
    if (now - start < 6000) requestAnimationFrame(tick);
    else finish();
  };
  tick();
}

function isBackForwardNavigation() {
  try {
    const entry = performance.getEntriesByType('navigation')[0];
    return !!entry && entry.type === 'back_forward';
  } catch (_) {
    return false;
  }
}

function savedData(fromHistory) {
  const st = history.state;
  if (fromHistory && st && st.hofAnchor) return st.hofAnchor;
  if (fromHistory && st && typeof st.scrollTop === 'number') return { top: st.scrollTop, id: '', off: 0 };
  try {
    const raw = sessionStorage.getItem(hofScrollKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'number' ? { top: parsed, id: '', off: 0 } : parsed;
  } catch (_) {
    return null;
  }
}

function startRestore(event) {
  const backForward = (event && event.persisted) || isBackForwardNavigation();
  let data = savedData(true);
  if (!data && backForward) data = savedData(false);
  if (!data || !(data.top > 0)) {
    document.documentElement.classList.remove('hof-restoring');
    return;
  }
  restoreWithRetry(data);
}

let hofStarted = false;
function earlyStart() {
  if (hofStarted) return;
  hofStarted = true;
  startRestore(null);
}
if (document.documentElement.classList.contains('hof-restoring')) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', earlyStart, { once: true });
  else earlyStart();
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    startRestore(event);
    return;
  }
  if (!hofStarted) {
    hofStarted = true;
    startRestore(event);
  }
});

const originalPushState = history.pushState.bind(history);
history.pushState = function (state, title, url) {
  saveScrollPosition();
  originalPushState(state, title, url);
};

window.addEventListener('popstate', () => {
  const data = savedData(true);
  if (!data) return;
  restoreWithRetry(data);
});

/**
 * Returns the appropriate target for listening to scroll events.
 * On desktop (≥990px), `.page-wrapper` emits scroll events directly.
 * On mobile, the document root scrolls natively and scroll events bubble to `document`.
 *
 * @returns {EventTarget} The target to call addEventListener('scroll', ...) on
 */
function getScrollEventTarget() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? document;
  }
  return document;
}

/**
 * Returns the appropriate root for an IntersectionObserver monitoring the scroll container.
 * On desktop (≥990px), `.page-wrapper` must be set as the explicit root.
 * On mobile, `null` uses the viewport root (the IntersectionObserver default).
 *
 * @returns {Element | null} The root option for IntersectionObserver
 */
function getIntersectionRoot() {
  if (SQUEEZE_QUERY.matches) {
    return document.querySelector(PAGE_WRAPPER_SELECTOR) ?? null;
  }
  return null;
}

export {
  getScrollContainer,
  getScrollTop,
  scrollTo,
  getScrollEventTarget,
  getIntersectionRoot,
  SQUEEZE_QUERY as scrollContainerMediaQuery,
};
